import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { InternalAppSummary } from './api/internalAppsApi';
import {
  useCreateInternalAppMutation,
  useListInternalAppsQuery,
  useRotateInternalAppSecretMutation,
  useUpdateInternalAppMutation,
} from './api/internalAppsApi';

type SecretNotice = {
  clientId: string;
  clientSecret: string;
  reason: 'created' | 'rotated';
};

type InternalAppFormState = {
  description: string;
  status: 'active' | 'inactive';
  allowedAudiencesText: string;
  allowedScopesText: string;
  rateLimitPerMinute: string;
  rateLimitPerHour: string;
  ipAllowlistText: string;
};

const DEFAULT_CREATE_AUDIENCES = 'moments';
const DEFAULT_CREATE_SCOPES = 'lifecycle:trigger';
const DEFAULT_RATE_LIMIT_PER_MINUTE = '100';
const DEFAULT_RATE_LIMIT_PER_HOUR = '1000';

const parseMultilineList = (value: string) =>
  value
    .split(/[\n,]/)
    .map((entry) => entry.trim())
    .filter(Boolean);

const formatListForTextarea = (values: string[] | undefined) => (values ?? []).join('\n');

const formatTimestamp = (value?: string | null) => {
  if (!value) {
    return 'Never';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
};

const toEditableState = (app: InternalAppSummary): InternalAppFormState => ({
  description: app.description ?? '',
  status: app.status,
  allowedAudiencesText: formatListForTextarea(app.allowed_audiences),
  allowedScopesText: formatListForTextarea(app.allowed_scopes),
  rateLimitPerMinute: String(app.rate_limit_per_minute),
  rateLimitPerHour: String(app.rate_limit_per_hour),
  ipAllowlistText: formatListForTextarea(app.ip_allowlist),
});

const parsePositiveInt = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 0) {
    throw new Error('Rate limits must be non-negative integers.');
  }
  return parsed;
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const data = 'data' in error ? (error as { data?: unknown }).data : undefined;
  if (data && typeof data === 'object') {
    const payload = data as { error_description?: unknown; message?: unknown; error?: unknown };
    if (typeof payload.error_description === 'string' && payload.error_description.trim()) {
      return payload.error_description;
    }
    if (typeof payload.message === 'string' && payload.message.trim()) {
      return payload.message;
    }
    if (typeof payload.error === 'string' && payload.error.trim()) {
      return payload.error;
    }
  }

  const message = 'message' in error ? (error as { message?: unknown }).message : undefined;
  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  return fallback;
};

export default function InternalAppsPage() {
  const { data, isFetching } = useListInternalAppsQuery();
  const [createInternalApp, { isLoading: isCreating }] = useCreateInternalAppMutation();
  const [updateInternalApp, { isLoading: isSaving }] = useUpdateInternalAppMutation();
  const [rotateInternalAppSecret, { isLoading: isRotating }] = useRotateInternalAppSecretMutation();

  const apps = data?.apps ?? [];
  const [selectedClientId, setSelectedClientId] = useState('');
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createClientId, setCreateClientId] = useState('');
  const [createAllowedAudiencesText, setCreateAllowedAudiencesText] =
    useState(DEFAULT_CREATE_AUDIENCES);
  const [createAllowedScopesText, setCreateAllowedScopesText] = useState(DEFAULT_CREATE_SCOPES);
  const [createRateLimitPerMinute, setCreateRateLimitPerMinute] = useState(
    DEFAULT_RATE_LIMIT_PER_MINUTE
  );
  const [createRateLimitPerHour, setCreateRateLimitPerHour] = useState(DEFAULT_RATE_LIMIT_PER_HOUR);
  const [createIPAllowlistText, setCreateIPAllowlistText] = useState('');
  const [editState, setEditState] = useState<InternalAppFormState | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [secretNotice, setSecretNotice] = useState<SecretNotice | null>(null);
  const [rotateDialogOpen, setRotateDialogOpen] = useState(false);
  const lastHydratedClientIdRef = useRef<string | null>(null);

  const selectedApp = useMemo(
    () => apps.find((app) => app.client_id === selectedClientId) ?? null,
    [apps, selectedClientId]
  );
  const createAudiences = useMemo(
    () => parseMultilineList(createAllowedAudiencesText),
    [createAllowedAudiencesText]
  );
  const createScopes = useMemo(
    () => parseMultilineList(createAllowedScopesText),
    [createAllowedScopesText]
  );
  const editAudiences = useMemo(
    () => parseMultilineList(editState?.allowedAudiencesText ?? ''),
    [editState?.allowedAudiencesText]
  );
  const editScopes = useMemo(
    () => parseMultilineList(editState?.allowedScopesText ?? ''),
    [editState?.allowedScopesText]
  );
  const canCreate =
    createName.trim() !== '' &&
    createClientId.trim() !== '' &&
    createAudiences.length > 0 &&
    createScopes.length > 0;
  const canSave =
    Boolean(selectedApp && editState) && editAudiences.length > 0 && editScopes.length > 0;

  useEffect(() => {
    if (apps.length === 0) {
      setSelectedClientId('');
      setEditState(null);
      lastHydratedClientIdRef.current = null;
      return;
    }

    if (!selectedClientId || !apps.some((app) => app.client_id === selectedClientId)) {
      setSelectedClientId(apps[0].client_id);
      return;
    }

    const currentSelectedApp = apps.find((app) => app.client_id === selectedClientId);
    if (currentSelectedApp && lastHydratedClientIdRef.current !== selectedClientId) {
      setEditState(toEditableState(currentSelectedApp));
      lastHydratedClientIdRef.current = selectedClientId;
    }
  }, [apps, selectedClientId]);

  const clearMessages = () => {
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  const handleSelectApp = (app: InternalAppSummary) => {
    setSelectedClientId(app.client_id);
    setEditState(toEditableState(app));
    lastHydratedClientIdRef.current = app.client_id;
    clearMessages();
  };

  const handleCreate = async () => {
    clearMessages();
    setSecretNotice(null);

    try {
      const response = await createInternalApp({
        name: createName.trim(),
        description: createDescription,
        client_id: createClientId.trim(),
        allowed_audiences: createAudiences,
        allowed_scopes: createScopes,
        rate_limit_per_minute: parsePositiveInt(createRateLimitPerMinute),
        rate_limit_per_hour: parsePositiveInt(createRateLimitPerHour),
        ip_allowlist: parseMultilineList(createIPAllowlistText),
      }).unwrap();

      setCreateName('');
      setCreateDescription('');
      setCreateClientId('');
      setCreateAllowedAudiencesText(DEFAULT_CREATE_AUDIENCES);
      setCreateAllowedScopesText(DEFAULT_CREATE_SCOPES);
      setCreateRateLimitPerMinute(DEFAULT_RATE_LIMIT_PER_MINUTE);
      setCreateRateLimitPerHour(DEFAULT_RATE_LIMIT_PER_HOUR);
      setCreateIPAllowlistText('');
      setSelectedClientId(response.client_id);
      setSecretNotice({
        clientId: response.client_id,
        clientSecret: response.client_secret,
        reason: 'created',
      });
      setSuccessMessage(
        `Created internal app ${response.client_id}. Save the revealed secret now.`
      );
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'Failed to create internal app.'));
    }
  };

  const handleSave = async () => {
    if (!selectedApp || !editState) {
      return;
    }

    clearMessages();

    try {
      await updateInternalApp({
        clientId: selectedApp.client_id,
        description: editState.description,
        status: editState.status,
        allowed_audiences: editAudiences,
        allowed_scopes: editScopes,
        rate_limit_per_minute: parsePositiveInt(editState.rateLimitPerMinute),
        rate_limit_per_hour: parsePositiveInt(editState.rateLimitPerHour),
        ip_allowlist: parseMultilineList(editState.ipAllowlistText),
      }).unwrap();
      setSuccessMessage(`Updated internal app ${selectedApp.client_id}.`);
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'Failed to update internal app.'));
    }
  };

  const handleRotate = async () => {
    if (!selectedApp) {
      return;
    }

    clearMessages();

    try {
      const response = await rotateInternalAppSecret({ clientId: selectedApp.client_id }).unwrap();
      setSecretNotice({
        clientId: response.client_id,
        clientSecret: response.client_secret,
        reason: 'rotated',
      });
      setSuccessMessage(`Rotated secret for ${response.client_id}. Save the revealed secret now.`);
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'Failed to rotate internal app secret.'));
    }
  };

  const handleRequestRotate = () => {
    if (!selectedApp) {
      return;
    }

    setRotateDialogOpen(true);
  };

  const handleCloseRotateDialog = () => {
    if (isRotating) {
      return;
    }

    setRotateDialogOpen(false);
  };

  const handleConfirmRotate = async () => {
    await handleRotate();
    setRotateDialogOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 3 }}>
      <Box>
        <Typography variant="h4" gutterBottom>
          Internal Apps
        </Typography>
        <Typography color="text.secondary">
          Manage service credentials for internal clients that use OAuth client credentials against
          Moments.
        </Typography>
      </Box>

      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}
      {secretNotice ? (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" onClick={() => setSecretNotice(null)} size="small">
              Dismiss
            </Button>
          }
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {secretNotice.reason === 'created' ? 'Created' : 'Rotated'} secret for{' '}
            {secretNotice.clientId}
          </Typography>
          <Typography component="code" sx={{ wordBreak: 'break-all' }}>
            {secretNotice.clientSecret}
          </Typography>
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', xl: 'minmax(320px, 360px) minmax(0, 1fr)' },
          alignItems: 'start',
        }}
      >
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h6">Create Internal App</Typography>
              <Typography variant="body2" color="text.secondary">
                New secrets are shown once at creation time. The default lifecycle audience and
                scope are prefilled for the Rails trigger client.
              </Typography>
            </Box>
            <TextField
              label="Name"
              value={createName}
              onChange={(event) => setCreateName(event.target.value)}
              fullWidth
            />
            <TextField
              label="Description"
              value={createDescription}
              onChange={(event) => setCreateDescription(event.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Client ID"
              value={createClientId}
              onChange={(event) => setCreateClientId(event.target.value)}
              fullWidth
            />
            <TextField
              label="Allowed audiences"
              value={createAllowedAudiencesText}
              onChange={(event) => setCreateAllowedAudiencesText(event.target.value)}
              fullWidth
              multiline
              minRows={2}
              helperText="One audience per line or comma-separated. At least one audience is required."
            />
            <TextField
              label="Allowed scopes"
              value={createAllowedScopesText}
              onChange={(event) => setCreateAllowedScopesText(event.target.value)}
              fullWidth
              multiline
              minRows={2}
              helperText="One scope per line or comma-separated. At least one scope is required."
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Rate limit per minute"
                value={createRateLimitPerMinute}
                onChange={(event) => setCreateRateLimitPerMinute(event.target.value)}
                fullWidth
              />
              <TextField
                label="Rate limit per hour"
                value={createRateLimitPerHour}
                onChange={(event) => setCreateRateLimitPerHour(event.target.value)}
                fullWidth
              />
            </Stack>
            <TextField
              label="IP allowlist"
              value={createIPAllowlistText}
              onChange={(event) => setCreateIPAllowlistText(event.target.value)}
              fullWidth
              multiline
              minRows={3}
              helperText="Optional. One CIDR or IP per line."
            />
            <Button variant="contained" onClick={handleCreate} disabled={isCreating || !canCreate}>
              Create internal app
            </Button>
          </Stack>
        </Paper>

        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
            <Box sx={{ p: 3, pb: 2 }}>
              <Typography variant="h6">Existing Apps</Typography>
              <Typography variant="body2" color="text.secondary">
                Review configured audiences, scopes, status, usage, and rate limits.
              </Typography>
            </Box>
            <Divider />
            {apps.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary">
                  {isFetching ? 'Loading internal apps...' : 'No internal apps found.'}
                </Typography>
              </Box>
            ) : (
              <List disablePadding aria-label="Internal apps list">
                {apps.map((app, index) => (
                  <Box key={app.client_id}>
                    {index > 0 ? <Divider component="li" /> : null}
                    <ListItemButton
                      selected={app.client_id === selectedClientId}
                      onClick={() => handleSelectApp(app)}
                      alignItems="flex-start"
                    >
                      <ListItemText
                        disableTypography
                        primary={app.name}
                        secondary={
                          <Stack spacing={1} sx={{ mt: 0.5 }}>
                            <Typography component="div" variant="body2" color="text.secondary">
                              {app.client_id}
                            </Typography>
                            {app.description ? (
                              <Typography component="div" variant="body2" color="text.secondary">
                                {app.description}
                              </Typography>
                            ) : null}
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                              <Chip
                                label={app.status === 'active' ? 'Active' : 'Inactive'}
                                color={app.status === 'active' ? 'success' : 'default'}
                                size="small"
                              />
                              <Chip label={`${app.token_count} tokens`} size="small" />
                              <Chip
                                label={`Last used: ${formatTimestamp(app.last_used_at)}`}
                                size="small"
                              />
                            </Stack>
                          </Stack>
                        }
                      />
                    </ListItemButton>
                  </Box>
                ))}
              </List>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h6">Manage Selected App</Typography>
                <Typography variant="body2" color="text.secondary">
                  Edit description, status, audiences, scopes, rate limits, and IP allowlist. Secret
                  rotation reveals a new secret one time.
                </Typography>
              </Box>

              {selectedApp && editState ? (
                <>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle1">{selectedApp.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedApp.client_id}
                    </Typography>
                  </Stack>

                  <TextField
                    label="Description"
                    value={editState.description}
                    onChange={(event) =>
                      setEditState((current) =>
                        current ? { ...current, description: event.target.value } : current
                      )
                    }
                    fullWidth
                    multiline
                    minRows={2}
                  />

                  <FormControl fullWidth>
                    <InputLabel id="internal-app-status-label">Status</InputLabel>
                    <Select
                      labelId="internal-app-status-label"
                      label="Status"
                      value={editState.status}
                      onChange={(event) =>
                        setEditState((current) =>
                          current
                            ? {
                                ...current,
                                status: event.target.value as 'active' | 'inactive',
                              }
                            : current
                        )
                      }
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label="Allowed audiences"
                    value={editState.allowedAudiencesText}
                    onChange={(event) =>
                      setEditState((current) =>
                        current
                          ? {
                              ...current,
                              allowedAudiencesText: event.target.value,
                            }
                          : current
                      )
                    }
                    fullWidth
                    multiline
                    minRows={2}
                    helperText="At least one audience is required."
                  />

                  <TextField
                    label="Allowed scopes"
                    value={editState.allowedScopesText}
                    onChange={(event) =>
                      setEditState((current) =>
                        current
                          ? {
                              ...current,
                              allowedScopesText: event.target.value,
                            }
                          : current
                      )
                    }
                    fullWidth
                    multiline
                    minRows={2}
                    helperText="At least one scope is required."
                  />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="Rate limit per minute"
                      value={editState.rateLimitPerMinute}
                      onChange={(event) =>
                        setEditState((current) =>
                          current
                            ? {
                                ...current,
                                rateLimitPerMinute: event.target.value,
                              }
                            : current
                        )
                      }
                      fullWidth
                    />
                    <TextField
                      label="Rate limit per hour"
                      value={editState.rateLimitPerHour}
                      onChange={(event) =>
                        setEditState((current) =>
                          current
                            ? {
                                ...current,
                                rateLimitPerHour: event.target.value,
                              }
                            : current
                        )
                      }
                      fullWidth
                    />
                  </Stack>

                  <TextField
                    label="IP allowlist"
                    value={editState.ipAllowlistText}
                    onChange={(event) =>
                      setEditState((current) =>
                        current
                          ? {
                              ...current,
                              ipAllowlistText: event.target.value,
                            }
                          : current
                      )
                    }
                    fullWidth
                    multiline
                    minRows={3}
                  />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      disabled={isSaving || !canSave}
                    >
                      Save changes
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={handleRequestRotate}
                      disabled={isRotating}
                    >
                      Rotate secret
                    </Button>
                  </Stack>
                </>
              ) : (
                <Typography color="text.secondary">
                  Select an internal app from the list to manage it.
                </Typography>
              )}
            </Stack>
          </Paper>
        </Stack>
      </Box>
      <Dialog open={rotateDialogOpen} onClose={handleCloseRotateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Rotate client secret?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Rotating the secret for {selectedApp?.client_id ?? 'this internal app'} invalidates the
            current credential immediately. Any running process still using it will fail until it is
            updated with the newly revealed secret.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRotateDialog} disabled={isRotating}>
            Cancel
          </Button>
          <Button onClick={handleConfirmRotate} color="warning" disabled={isRotating}>
            Confirm rotate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
